<template>
  <div>
    <a-table
      class="table"
      :columns="columnsTable"
      :data-source="usersInfo.items"
      :loading="loadingTable"
      size="small"
      row-key="id"
      :scroll="scrollStyle"
      :pagination="usersInfo.pagination"
      @change="handleTableChange"
    >
      <template #headerCell="{ column }">
        <template v-if="column.key === 'perfilONivel'">
          Perfil
          <SwapOutlined :rotate="90" />
        </template>

        <template v-if="column.key === 'status'">
          <span> Estado <SwapOutlined :rotate="90" /> </span>
        </template>
      </template>

      <template #type_document="{ record }">
        <span>{{
          record.type_document == 1
            ? "DNI"
            : record.type_document == 2
            ? "Carnet de extranjería"
            : "Pasaporte"
        }}</span>
      </template>

      <template #fullname="{ record }">
        <span style="display: flex; align-items: center; gap: 8px;">
          <UserAvatar size="28px" class="avatar" :record="{ user: record }" />
          {{ record.fullname }}
        </span>
      </template>

      <template #statusScoped="{ record }">
        <span>
          <a-tag :color="statusColor(record.status)">
            {{ statusText(record.status) }}
          </a-tag>
        </span>
      </template>

      <template #company="{ record }">
        <a-popover placement="bottom" v-if="record.entities?.length > 0">
          <template #content>
            <ul class="container__entity">
              <li v-for="item of record.entities" :key="item.id" class="separator">
                <img v-if="item.logo" class="image" :src="item.logo" alt="logo" />
                <div v-else class="logo__default" />
                {{ item.name }}
              </li>
            </ul>
          </template>
          <template #title>
            <span>Empresas</span>
          </template>
          <span>
            {{ entity(record.entities) }}
          </span>
        </a-popover>
        <span v-if="record.entities?.length < 1">
          {{ entity(record.entities) }}
        </span>
      </template>

      <template #cartera="{ record }">
        <a-popover placement="bottom" v-if="record.portfolios?.length > 0">
          <template #content>
            <ul class="container__campaign">
              <li v-for="item of record.portfolios" :key="item.id">
                {{ item.portfolio }}
              </li>
            </ul>
          </template>
          <template #title>
            <span>Campañas</span>
          </template>
          <span>
            {{ campaign(record.portfolios) }}
          </span>
        </a-popover>
        <span v-if="record.portfolios?.length < 1">
          {{ campaign(record.portfolios) }}
        </span>
      </template>

      <template #actionScoped="{ record }">
        <span class="flex__items">
          <ActiveUserSwitch
            :record="record"
            :disabled="disabledInactiveSwitch(record) || record.status == UserStatus.pending"
            :tooltip="tooltipInactiveSwitch(record, 'I')"
            @update="handleUpdate"
            @openBuyModal="$emit('openBuySlotsModal', 1)"
          />

          <a-tooltip
            placement="top"
            :title="getCanEditAccount(record) ? 'Editar' : 'Ver'"
            class="user_action"
          >
            <a-button type="link" @click="handleShowModalWithRecord($event, record)">
              <template #icon>
                <EyeOutlined v-if="!getCanEditAccount(record)" />
                <EditOutlined v-else />
              </template>
            </a-button>
          </a-tooltip>
          <a-tooltip 
            placement="top" 
            :title="tooltipInactiveSwitch(record, 'E') || 'Eliminar'"
            class="user_action" 
            v-if="!record.is_owner">
            <a-button type="link" :disabled="disabledInactiveSwitch(record)" @click="handleShowModalDelete($event, record)">
              <template #icon>
                <DeleteOutlined />
              </template>
            </a-button>
          </a-tooltip>
          <!-- <a-tooltip
            placement="top"
            title="Reactivar"
            class="user_action"
            v-if="statusInactive(record)"
            :record="record"
          >
            <a-button type="link" @click="handleConfirmationReactivate(record.id, record)">
              <template #icon>
                <InteractionTwoTone />
              </template>
            </a-button>
          </a-tooltip> -->
        </span>
      </template>
    </a-table>
    <users-invite-update
      :record="record"
      v-if="inviteUpdate"
      :visible="inviteUpdate"
      @update="handleUpdate"
      @onCloseModal="handleCloseModalWithRecord"
      @delete="handleShowModalDelete('event', $event)"
    ></users-invite-update>
    <delete-user-modal
      v-if="
        recordProfile?.role?.id === RolTypes.administrator && recordProfile.assigned_contacts === 0
      "
      :record="recordProfile"
      :visible="visibleDelete"
      type="User"
      @onCancel="handleCancelDelete"
      @onCloseModal="handleCancel"
      @update="handleUpdate"
    ></delete-user-modal>
    <delete-user-modal
      v-if="recordProfile?.role?.id === RolTypes.agent && recordProfile.assigned_contacts === 0"
      :visible="visibleDelete"
      :record="recordProfile"
      @onCancel="handleCancelDelete"
      @onCloseModal="handleCancel"
      @update="handleUpdate"
    />
    <DeleteAgentAssigned
      v-if="recordProfile && recordProfile?.assigned_contacts > 0"
      :visible="visibleDelete"
      :record="recordProfile"
      @onCancel="handleCancelDelete"
      @onCloseModal="handleCancel"
      @update="handleUpdate"
    />
  </div>
</template>

<script>
import { defineComponent, createVNode, computed, inject } from "vue";
import {
  SwapOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons-vue";
import { Modal } from "ant-design-vue";
import { useStore } from "vuex";

import { UserStatus, RolTypes } from "@/app/shared/utils/enums";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { getUserTableColumn } from "./UsersTableColumns";

import DeleteUserModal from "@/app/users/components/modals/DeleteUserModal.vue";
import UsersInviteUpdate from "@/app/users/components/modals/UsersInviteUpdate.vue";
import DeleteAgentAssigned from "@/app/users/components/modals/DeleteAgentAssigned.vue";
import ActiveUserSwitch from "@/app/users/components/buttons/ActiveUserSwitch.vue";
import UserAvatar from "@/app/shared/components/avatar/UserAvatar.vue";

export default defineComponent({
  components: {
    SwapOutlined,
    UsersInviteUpdate,
    EditOutlined,
    EyeOutlined,
    DeleteOutlined,
    DeleteUserModal,
    DeleteAgentAssigned,
    ActiveUserSwitch,
    UserAvatar,
  },
  setup() {
    const store = useStore();
    const user = computed(() => store.getters["getDataUser"]);

    const loadingTable = inject("loadingTable");
    const formState = inject("formState");
    const { scrollStyle } = useScrollResize({ headerMargin: 350 });

    // const scrollStyle = ref({ x: 1500, y: 500 });

    // const onResize = () => {
    //   scrollStyle.value = { x: 1500, y: window.innerHeight - 330 };
    // };

    // onMounted(() => {
    //   window.addEventListener("resize", onResize);
    //   scrollStyle.value = { x: 1500, y: window.innerHeight - 330 };
    // });

    // onBeforeUnmount(() => {
    //   window.removeEventListener("resize", onResize);
    // });

    return {
      scrollStyle,
      user,
      RolTypes,
      loadingTable,
      formState,
    };
  },
  data() {
    return {
      UserStatus,
      record: null,
      recordProfile: null,
      visibleDelete: false,
      inviteUpdate: false,
    };
  },
  computed: {
    usersInfo() {
      return this.$store.getters["getUsersInfo"];
    },
    columnsTable() {
      return getUserTableColumn(this.user?.customer?.type_business);
    },
    user() {
      return this.$store.getters["getDataUser"];
    },
    isOwner() {
      return this.user?.is_owner || this.user?.role?.id == RolTypes.owner;
    },
    isAdmin() {
      return this.user?.role?.id == RolTypes.administrator;
    },
  },
  async mounted() {
    await this.handleFetchUsers(this.pagination);
  },
  methods: {
    handleUpdate() {
      this.$emit("update", this.usersInfo.pagination);
    },
    getProfileImage(profile) {
      return new URL(`../../../users/assets/profiles/${profile}`, import.meta.url).href;
    },
    handleCloseModalWithRecord() {
      this.record = null;
      this.inviteUpdate = false;
    },
    handleShowModalWithRecord(event, record) {
      this.record = record;

      this.inviteUpdate = true;
    },
    entity(array) {
      if (!array || array.length == 0) return "Sin Empresas";
      if (array.length == 1) return "1 empresa";
      return `${array.length} Empresas`;
    },
    campaign(array) {
      if (!array || array.length == 0) return "Sin campañas";
      if (array.length == 1) return "1 campaña";
      return `${array.length} campañas`;
    },
    async handleTableChange(pagination) {
      this.loadingTable = true;

      await this.handleFetchUsers(pagination);

      this.loadingTable = false;
    },
    async handleFetchUsers(pagination) {
      await this.$store.dispatch("fetchAllFilterProfile", {
        search: this.formState?.search || "",
        profile: this.formState?.selectedProfile || 0,
        pagination,
      });
      this.$emit("created");
    },
    statusInactive(record) {
      return record.status === UserStatus.inactive;
    },
    statusColor(status) {
      let value;
      switch (status) {
        case UserStatus.active:
          value = "green";
          break;
        case UserStatus.inactive:
          value = "red";
          break;
        case UserStatus.delete:
          value = "red";
          break;
        case UserStatus.pending:
          value = "default";
          break;
      }
      return value;
    },
    statusText(status) {
      let value;
      switch (status) {
        case UserStatus.active:
          value = "Activo";
          break;
        case UserStatus.inactive:
          value = "Inactivo";
          break;
        case UserStatus.delete:
          value = "Eliminado";
          break;
        case UserStatus.pending:
          value = "Pendiente";
          break;
      }
      return value;
    },
    entityFix(portfoliosArray) {
      return portfoliosArray?.map((object) => {
        return object.entity;
      });
    },
    campaignFix(campaignsArray) {
      return campaignsArray?.map((...object) => {
        return object.portfolio;
      });
    },
    handleConfirmationReactivate(id, record) {
      Modal.confirm({
        title: `¿Estás seguro de reactivar a ${record.first_name} ${record.last_name}?`,
        content:
          "Recuerda que el usuario podrá volver a acceder a la plataforma y se consumirá un nuevo slot.",
        icon: createVNode(QuestionCircleOutlined),
        onOk: () => this.updateStatus(id),
        centered: true,
        okText: "Reactivar",
        cancelText: "Cancelar",
        onCancel: () => {
          this.visible = false;
        },
      });
    },
    async updateStatus(id) {
      try {
        const body = {
          status: UserStatus.active,
        };
        await this.$store.dispatch("updateUser", {
          id,
          params: body,
        });
        this.handleUpdate();
      } catch (error) {
        let errorMessage = "Ha ocurrido un error";

        if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

        this.$message.error(errorMessage);
        // TODO: show error in the UI
      }
    },
    handleShowModalDelete(event, record) {
      this.recordProfile = record;
      if (record.status === 4) {
        this.record = record;
        Modal.confirm({
          title: `¿Estás seguro de eliminar la invitación a “${this.record?.email}”?`,
          content:
            "Recuerda que este usuario ya no podrá unirse a tu equipo de trabajo. Si deseas volver a invitarlo, podrás hacerlo haciendo click en el botón de “Invitar usuarios”.",
          icon: createVNode(QuestionCircleOutlined),
          onOk: async () => {
            await this.deleteInviteUser();
            this.handleUpdate();
          },
          centered: true,
          okText: "Eliminar",
          cancelText: "Cancelar",
        });
      } else {
        this.visibleDelete = true;
      }
    },
    handleCancelDelete() {
      this.visibleDelete = false;
      this.visibleDelete = null;
    },
    handleCancel() {
      this.visibleDelete = false;
      this.visibleDelete = null;
    },
    handleOpenInviteUpdate() {
      this.inviteUpdate = true;
    },
    async deleteInviteUser() {
      try {
        await this.$store.dispatch("deleteUser", this.record.id);
        this.inviteUpdate = false;
        this.$message.success("Se elimino la invitación correctamente");
      } catch (error) {
        this.$message.error("Error al eliminar el usuario");
      }
    },
    getCanEditAccount(record) {
      return this.user?.role?.id === RolTypes.owner ? true : record?.role?.id !== RolTypes.owner;
    },
    disabledInactiveSwitch(record) {
      return (this.isAdmin && record.profile == RolTypes.owner) || 
        (this.isAdmin && record.id == this.user.id) ||
        (this.isOwner && record.id == this.user.id);
    },
    tooltipInactiveSwitch(record, action) {
      return this.isAdmin && record.profile == RolTypes.owner ? 
        'No puedes desactivar la cuenta\n del owner.' :
        (this.isAdmin && record.id == this.user.id ?
          `No puedes ${action == 'I' ? 'desactivar' : 'eliminar'} tu propia cuenta. 
          Solicita a otro usuario, que sea owner 
          o administrador, que realice esta acción.` :
          (
            this.isOwner && record.id == this.user.id ?
            `No puedes desactivar tu propia cuenta. Transfiere tu rol de owner a 
            otro usuario para que lo realice. Haz el cambio desde Seguridad y Permisos.` :
            null
          )
        );
    },
  },
});
</script>

<style lang="sass" scoped>
:deep(.ant-table-thead > tr > th .ant-table-column-sorter)
  display: none
// :deep(.ant-table-tbody > tr > td)
//   padding: 8px 10px

:deep(.ant-table-thead > tr > th)
  // padding: 10px !important

// .ant-table-body
//   overflow-x: auto !important

.flex__items
  display: flex
  align-items: center
  gap: 5px
</style>

<style lang="sass">
:deep(.ant-table-tbody > tr.ant-table-row-hover:not(.ant-table-expanded-row):not(.ant-table-row-selected) > td)
  background: #feffe6 !important

.user_action
  // padding-bottom: 10px !important

.container__entity
  list-style: none
  // padding: 10px
  // margin-bottom: 10px

.container__campaign
  // padding-left: 30px
  color: $gray-8
  // padding-top: 10px
  // padding-bottom: 10px

.image
  width: 24px
  height: 24px
  border-radius: 50%
  margin-right: 12px

.separator
  // margin-bottom: 10px
  display: flex

.logo__default
  width: 25.63px
  height: 25.24px
  background-size: cover
  background-position: center
  border-radius: 50%
  // margin-right: 12px
  background-image: url('../../assets/briefcase-blue.svg')
</style>
